import { Button, ErrorBanner } from '@/shared/ui';
import { useReferenceReview } from '@/features/references/hooks/useReferenceReview';
import { isLiveOp } from '@/features/references/services/referencesApi';
import { ReferenceCharacterCard } from '@/features/references/components/ReferenceCharacterCard';

/**
 * The `awaiting_reference_review` screen: the engine has drawn every character from
 * three angles and stopped before rendering any scene. The user can regenerate a
 * character with notes (a whole new three-angle version, never a mix) and then approves
 * the exact versions on screen.
 *
 * Replacing a character with an uploaded photo is a deliberate v1 descope. When it is
 * approved it belongs on the card beside "Regenerate with notes"; nothing here assumes
 * regeneration is the only way a version can come to exist.
 */
export type ReferenceReviewPanelProps = {
  jobId: string;
  approve: (jobId: string, reviewedVersions: Record<string, number>) => Promise<void>;
};

const formatExpiry = (iso: string): string | null => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
};

export function ReferenceReviewPanel({ jobId, approve }: ReferenceReviewPanelProps) {
  const r = useReferenceReview({ jobId, approve });
  const review = r.review;

  const anyLive = review?.characters.some((c) => isLiveOp(c.activeOp)) ?? false;
  const locked = anyLive || r.submitting !== null || r.approving;
  const expiry = review?.expiresAt ? formatExpiry(review.expiresAt) : null;

  return (
    <section className="ref-panel" aria-labelledby="ref-panel-title" aria-busy={r.loading}>
      <h2 id="ref-panel-title" className="ref-panel__title">
        Review your characters
      </h2>
      {/* Always mounted so assistive tech has a live region to watch: it announces when a
          regeneration lands, which otherwise only changes pictures on the screen. */}
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {r.announcement}
      </p>
      <p className="ref-panel__intro">
        Your characters are drawn. Check each one, regenerate with notes if something is off,
        then approve to start rendering scenes.
      </p>
      {expiry ? (
        <p className="ref-panel__expiry">
          This review expires on {expiry} if you take no action. Unused credits are returned.
        </p>
      ) : null}

      {r.loadError ? (
        <div className="ref-panel__load-error">
          <ErrorBanner message={r.loadError} />
          <Button size="sm" onClick={() => void r.reload()}>
            Try again
          </Button>
        </div>
      ) : null}

      {r.loading ? (
        <div className="ref-panel__grid" role="status" aria-label="Loading characters">
          {[0, 1].map((n) => (
            <div key={n} className="ref-card ref-card--loading">
              <div className="ref-skeleton ref-skeleton--line" />
              <div className="ref-card__images">
                <div className="ref-skeleton" />
                <div className="ref-skeleton" />
                <div className="ref-skeleton" />
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {review ? (
        <>
          {review.characters.length === 0 ? (
            <p className="ref-panel__empty">
              There are no characters in this video to review. You can continue.
            </p>
          ) : null}
          <div className="ref-panel__grid">
            {review.characters.map((character) => (
              <ReferenceCharacterCard
                key={`${character.charKey}:${character.version}`}
                character={character}
                locked={locked}
                submitting={r.submitting === character.charKey}
                jobRemaining={review.regenerationsRemainingForJob}
                maxNotesChars={review.maxNotesChars}
                error={r.charErrors[character.charKey]}
                dismissedOps={r.dismissedOps}
                onDismissOp={r.dismissOp}
                onRegenerate={(notes) => void r.regenerate(character.charKey, notes)}
              />
            ))}
          </div>

          {r.staleNotice ? (
            <p className="ref-panel__stale" role="alert">
              {r.staleNotice}
            </p>
          ) : null}
          {r.approveError ? <ErrorBanner message={r.approveError} /> : null}

          <div className="ref-panel__footer">
            <Button
              variant="primary"
              onClick={() => void r.approve()}
              loading={r.approving}
              disabled={!review.canApprove || anyLive || r.submitting !== null || r.approving}
            >
              Approve and continue
            </Button>
            {anyLive ? (
              <span className="ref-panel__hint">Wait for the current regeneration to finish.</span>
            ) : null}
          </div>
        </>
      ) : null}
    </section>
  );
}
